import { FileDown, FilePlus2, Loader2, Minus, Plus, Trash2 } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Tip } from "@/components/ui/overlays";
import { keys, useMarkup, useMarkupConfig } from "@/hooks/queries";
import { api, queryClient, type Annotation } from "@/lib/api";
import { useAnnotationTool } from "@/stores/annotation-tool";
import { useShell } from "@/stores/shell";

import { AnnotationToolbar } from "./AnnotationToolbar";
import { drawPageAnnotations } from "./annotation-overlay";
import { loadFabric, PageEditor, type EditorOpts, type PersistHandlers } from "./fabric-editor";
import {
  editorOpts,
  useAnnotationStyle,
  useEditorToolSync,
} from "./use-editor-tool-sync";
import { InfiniteCanvasView } from "./InfiniteCanvasView";
import { PageTitle } from "./PageTitle";
import type { InkModel } from "./ink-model";
import { fingerDrawActive } from "./input-policy";
import { isLightPaper, normalizeInfinitePaper, normalizePaper, PAPER_UNIT, paperBackground, type PaperStyle } from "./paper";
import { isInkTool, PenDownBuffer } from "./pen-buffer";
import { isPenStrokeActive } from "./stylus-state";
import { useViewportGestures } from "./use-viewport-gestures";
import { clamp, ViewportController } from "./viewport-controller";
import { flushViewState, loadViewState, rememberedTool, saveViewState, type ReaderViewState } from "./view-state";
import { useViewportScrollbars } from "./viewport-scrollbars";
import {
  OVERLAY_BAND_MARGIN_PX,
  VIRTUALIZE_MIN_PAGES,
  inViewportBand,
  pagesInBand,
} from "./visible-band";

// The loaded Fabric module type, without importing `fabric` at module scope.
type Fabric = Awaited<ReturnType<typeof loadFabric>>;

// One page is US-Letter (8.5 × 11) shaped in virtual units; annotation x/y
// normalize to these, so PDF export renders in this space. A canvas is a paged
// notebook — pages stack vertically and can be added/removed.
const BASE_W = 1700;
const BASE_H = 2200;
const GAP = 28; // vertical gap between stacked pages (committed px)
const TOP_GAP = 24; // px above the first page when seated (clears its badge)

/** The markup toolbar's footprint at the top of the surface, in px.
 *
 * `top-2` (8) + `py-1` twice (8) + a 32px control row + a little air. The
 * toolbar floats OVER the scroller, so nothing about the page layout knows it
 * is there; this constant is how the seating math finds out. Keep it in step
 * with AnnotationToolbar's anchor and ISLAND_BTN. */
const TOOLBAR_CLEARANCE = 56;

/** How far below the container's top edge the first page is seated.
 *
 * Two things have to clear, and both sit ABOVE the paper:
 *
 *   • the note's TITLE, which heads page 1 from the margin (see PageTitle — on
 *     the margin, because the sheet itself is an ink surface). Proportional to
 *     the page for the same reason the title is: both ride the zoom transform.
 *   • the markup toolbar, which floats over the top of the surface. Seated at
 *     the bare 24px the page — and then the title above it — opened underneath
 *     the toolbar, so the first thing you saw of a note was its title already
 *     half covered.
 *
 * Reserved whether or not the toolbar is currently up, so the page does not
 * jump down the screen the moment you switch from Read to Write. */
function topGapFor(pageW: number, hasTitle: boolean): number {
  const title = hasTitle ? pageW * 0.082 : 0;
  return Math.max(TOP_GAP, TOOLBAR_CLEARANCE + title);
}
const MIN_SCALE = 0.3;
const MAX_SCALE = 2.5;
// The server's per-canvas page cap (documents.py insert_canvas_page). Clamp the
// client so pageCount can never drift past what the endpoints will accept (plan F9).
const MAX_PAGES = 100;

/** Stack `count` identical pages vertically at `pageW × pageH`, `gap` between
 * them, and report the world content box (in committed px). All pages share a
 * size, so this is a near one-liner — but keeping it pure + exported mirrors
 * PdfViewer's testable layout and pins the scrollbar/centre math. */
export function canvasContentSize(
  count: number,
  pageW: number,
  pageH: number,
  gap: number,
): { contentW: number; contentH: number } {
  const n = Math.max(0, count);
  return { contentW: pageW, contentH: n > 0 ? n * pageH + (n - 1) * gap : 0 };
}

export interface CanvasViewProps {
  docId: string;
  background: string;
  persist: PersistHandlers;
  /** The per-document InkModel (north-star C): editors seed from it, read-only
   * overlays render from it, and its events route to the owning page editor. */
  model: InkModel;
  initialPages?: number;
  /** "pages" (a US-Letter notebook stack, the default) or "infinite" (a single
   * viewport-sized camera surface — plan F7). A legacy doc's null layout ⇒ pages. */
  layout?: "pages" | "infinite" | null;
  /** Paper style rendered (pure CSS) behind the ink — plan §3.5. A legacy doc's
   * null paper ⇒ blank. */
  paper?: string | null;
  /** The note's name. Shown in this surface's own chrome row while FULLSCREEN,
   * where the document title bar is collapsed and nothing else names the note. */
  title?: string;
  /** Cell spacing as a multiple of PAPER_UNIT — the note's own setting. */
  paperSpacing?: number;
  /** Rename, for the title written on page 1. People reach for the top-left of
   *  the paper to name a note; see PageTitle. */
  onRename?: (next: string) => void;
  /** Stamped under the title, the way a notebook page carries its date. */
  createdAt?: string | null;
}

/** Blank-note canvas. Dispatches on the note's layout: an infinite canvas is a
 * wholly different render (ONE camera-driven page-0 editor whose canvas fills the
 * viewport), so it lives in its own component reusing the SAME controller/editor/
 * model/pen-buffer/scrollbar machinery; the paged notebook below is unchanged. */
export function CanvasView(props: CanvasViewProps) {
  if (props.layout === "infinite") {
    return (
      <InfiniteCanvasView
        docId={props.docId}
        background={props.background}
        persist={props.persist}
        model={props.model}
        paper={normalizeInfinitePaper(props.paper)}
        spacing={props.paperSpacing}
        title={props.title}
      />
    );
  }
  return <PagedCanvasView {...props} />;
}

/** OneNote-style paged blank-note canvas: a stack of print-size pages the annotation
 * layer draws on. Reuses the per-page Fabric editor + read-only overlay, and —
 * like PdfViewer — adopts the ViewportController: continuous GPU pinch,
 * pointer-anchored zoom, momentum, and custom scrollbars over a transformed
 * "world" (see docs/architecture/frontend/zoom-and-raster-pipeline.md). Pages keep a COMMITTED scale (their
 * div pixel size); the live pinch delta rides the CSS transform and a settled
 * zoom just re-sizes the divs + the long-lived editors in place — never rebuilds
 * them (north-star A). */
function PagedCanvasView({
  docId,
  background,
  persist,
  model,
  initialPages = 1,
  paper,
  title,
  paperSpacing = 1,
  onRename,
  createdAt,
}: CanvasViewProps) {
  const paperStyle: PaperStyle = normalizePaper(paper);
  const fullscreen = useShell((s) => s.fullscreen);
  const markup = useMarkup(docId);
  const config = useMarkupConfig();
  const editable = config.data?.enabled ?? false;
  const fonts = config.data?.fonts ?? [];

  const [pageCount, setPageCount] = useState(Math.max(1, initialPages));
  // Restore the per-doc view state (F8) once, on first mount: seed the committed
  // scale from the saved zoom (clamped) and hold the saved camera to seat below.
  // The tool is NOT auto-applied — the doc still opens read-mode (P0); the saved
  // tool only feeds the pen-down auto-activate (F1.4).
  const restoreRef = useRef<ReaderViewState | null | undefined>(undefined);
  if (restoreRef.current === undefined) restoreRef.current = loadViewState(docId);
  const initialScale = restoreRef.current ? clamp(restoreRef.current.zoom, MIN_SCALE, MAX_SCALE) : 0.5;
  // The scale the page divs are SIZED at (committed). The live view scale lives
  // in the controller; the CSS world transform carries the delta between them —
  // exactly like PdfViewer's rasterScale, except a canvas page is a blank div, so
  // a "commit" is a cheap re-size, not a pdf.js re-raster. Default 0.5 fits a
  // US-Letter page within the reader pane.
  const [committedScale, setCommittedScale] = useState(initialScale);
  // Live scale for the toolbar % readout (throttled from the controller).
  const [displayScale, setDisplayScale] = useState(initialScale);
  const [saving, setSaving] = useState(false);
  const [pagesBusy, setPagesBusy] = useState(false);

  // One subscription set, shared by all three host surfaces.
  const style = useAnnotationStyle();
  const { tool } = style;
  const editMode = editable && tool !== "none";
  const editModeRef = useRef(editMode);
  editModeRef.current = editMode;
  const editableRef = useRef(editable);
  editableRef.current = editable;

  // The InkModel is the source of truth for editors + overlays; keep a ref so the
  // stable reconcile/commit paths read the current one without re-subscribing.
  const modelRef = useRef(model);
  modelRef.current = model;
  // Editors must seed from the model, so don't mount them until the first fetch
  // settles and the model is seeded — otherwise entering edit mode before the GET
  // resolves seeds every editor empty and existing strokes stay invisible for the
  // whole session (the read-only overlay is suppressed while editing). `isLoading`
  // is true only on that first fetch, so this flips once and never rebuilds editors.
  const markupReady = !markup.isLoading;

  // Long-lived per-page editors + their host divs (north-star A). A zoom commit
  // resizes each editor in place; only edit-mode exit or a page removal destroys
  // one. `fabricRef` holds the loaded module so a page add can reconcile without
  // reloading it.
  const pageEls = useRef<Map<number, HTMLDivElement>>(new Map());
  const editors = useRef<Map<number, PageEditor>>(new Map());
  const fabricRef = useRef<Fabric | null>(null);

  const containerRef = useRef<HTMLDivElement | null>(null);
  // The transformed "world" the page stack lives in; the controller writes its
  // matrix here.
  const worldRef = useRef<HTMLDivElement | null>(null);
  const controllerRef = useRef<ViewportController | null>(null);
  // Bridges the first-edit dead zone: records a pen contact while edit mode is on
  // but the editors haven't attached yet, then replays it into them (F1.3).
  const penBufferRef = useRef<PenDownBuffer | null>(null);
  if (!penBufferRef.current) penBufferRef.current = new PenDownBuffer();
  // The next layout should SEAT (factor forced to 1) rather than fold a zoom
  // delta — set on first mount only, so a page add/zoom keeps the scroll position.
  const seatPendingRef = useRef(true);

  // Refs the stable reconcile/commit paths read without re-subscribing.
  const pageCountRef = useRef(pageCount);
  pageCountRef.current = pageCount;
  const optsRef = useRef<EditorOpts>(editorOpts(style));
  optsRef.current = editorOpts(style);

  const { updateScrollbars, scrollbars } = useViewportScrollbars(controllerRef);

  /** Hide EVERY page's read overlay. Used at the moment write mode opens, before
   *  Fabric has loaded and before any editor exists.
   *
   *  drawOverlays cannot do this job: it keeps the overlay for any page without a
   *  live editor, which is exactly right once the editors are up and exactly wrong
   *  here. Calling it at entry rebuilt the full SVG for every page — every stroke in
   *  the document — only to throw it away a moment later when Fabric mounted. That
   *  cost 22 percent of the entry on a 1-page note with 5,000 strokes. `[measured]`
   *  2026-09-19. */
  const clearOverlays = useCallback(() => {
    for (const [, el] of pageEls.current) {
      try {
        drawPageAnnotations(el, []);
      } catch {
        /* markup must never break the canvas */
      }
    }
  }, []);

  /** Pages that have an editor but have not painted through it yet. They keep the
   *  read overlay until Fabric's first render lands, so the handover shows ink the
   *  whole way through instead of a blank frame. */
  const handoverPending = useRef<Set<number>>(new Set());

  const drawOverlays = useCallback(() => {
    const root = containerRef.current;
    for (const [p, el] of pageEls.current) {
      // Only a page with a LIVE editor hides its SVG: Fabric is drawing that page
      // instead. Every other page keeps the read-only overlay, which is what makes
      // editor virtualization invisible — ink outside the viewport band is still on
      // screen, it is just not editable until you scroll to it.
      // A page whose editor was JUST built keeps its SVG for two frames: see
      // handoverPending. Removing it in the same frame leaves the page blank until
      // Fabric's first render lands, which reads as a flash while scrolling.
      const live =
        editModeRef.current && editors.current.has(p) && !handoverPending.current.has(p);
      if (live) {
        try {
          drawPageAnnotations(el, []);
        } catch {
          /* markup must never break the canvas */
        }
        continue;
      }
      // Build the SVG only for pages on or near the screen. This loop had no
      // viewport test, so opening an 80-page note built 80,000 SVG paths in one
      // synchronous pass and the document never became usable. PdfViewer has always
      // virtualized its overlay this way.
      //
      // The OVERLAY band, which is far wider than the editor band. A page that just
      // lost its editor has to be comfortably inside this one, or it loses its ink
      // as well and the stroke vanishes mid-scroll.
      if (root && !inViewportBand(root, el, OVERLAY_BAND_MARGIN_PX)) continue;
      try {
        drawPageAnnotations(el, modelRef.current.byPage(p));
      } catch {
        /* markup must never break the canvas */
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Feed the saved-markup fetch into the model: SEED on the first load, RECONCILE
  // after (merge unseen rows / refresh untouched ones, never remove local ink),
  // then redraw the read-only overlay. Declared BEFORE the editor-mount effect so
  // the model is seeded when editors read byPage().
  useEffect(() => {
    if (markup.isLoading) return;
    const data = markup.data ?? [];
    if (!model.isSeeded()) model.seed(data);
    else model.reconcile(data);
    drawOverlays();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markup.data, markup.isLoading, model]);

  // Route model events to the owning page editor while editing (an undo/redo or a
  // reconcile the editor didn't originate), or redraw the read-only overlay while
  // reading. The editor already reflects its OWN mutations, so those emit nothing.
  useEffect(() => {
    const editorFor = (page: number) => editors.current.get(page) ?? null;
    const offs = [
      // A page with no live editor is showing the SVG overlay, so it redraws the
      // same way read mode does. Without this an undo that touches an offscreen
      // page would change the model and nothing on screen.
      model.on("added", (a) => {
        const ed = editModeRef.current ? editorFor(a.page) : null;
        if (ed) ed.applyExternalAdd(a);
        else drawOverlays();
      }),
      model.on("removed", (id, page) => {
        const ed = editModeRef.current ? editorFor(page) : null;
        if (ed) ed.applyExternalRemove(id);
        else drawOverlays();
      }),
      model.on("replaced", (a) => {
        const ed = editModeRef.current ? editorFor(a.page) : null;
        if (ed) ed.applyExternalReplace(a);
        else drawOverlays();
      }),
      model.on("idChanged", (tempId, serverId, page) => {
        if (editModeRef.current) editorFor(page)?.applyIdChange(tempId, serverId);
      }),
    ];
    return () => offs.forEach((off) => off());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model]);

  // Construct the controller once (min 0.3, max 2.5). onCommit adopts the settled
  // scale; canCommit defers it while a pen stroke is mid-flight so a page is never
  // re-sized under an active stroke. onChange (rAF-throttled) keeps the % readout
  // and the scrollbars current.
  const scheduleReconcileRef = useRef<() => void>(() => {});
  const fillOverlaysRef = useRef<() => void>(() => {});
  if (!controllerRef.current) {
    let raf = 0;
    controllerRef.current = new ViewportController({
      min: MIN_SCALE,
      max: MAX_SCALE,
      onCommit: (s) => setCommittedScale(s),
      canCommit: () => !isPenStrokeActive(),
      onChange: () => {
        if (raf) return;
        raf = requestAnimationFrame(() => {
          raf = 0;
          const c = controllerRef.current;
          if (c) setDisplayScale(c.scale);
          updateScrollbars();
          // Persist the per-doc zoom/camera/tool (debounced) so a refresh restores
          // where the user was (F8).
          if (c) {
            saveViewState(docId, {
              zoom: c.scale, tx: c.tx, ty: c.ty, tool: optsRef.current.tool, updatedAt: Date.now(),
            });
          }
          // The camera moved, so the set of pages in the band may have changed.
          // Trailing-timed, not per frame: mounting an editor during a fling would
          // cost more than the mount it saves.
          scheduleReconcileRef.current();
          // And in READ mode, where there are no editors to reconcile, the overlay
          // still has to fill the pages the pan revealed.
          fillOverlaysRef.current();
        });
      },
    });
  }

  // Arm the pen-down buffer for the whole annotatable session (F1.3 + F1.4): in
  // read mode the first PEN contact auto-activates the remembered tool; in edit
  // mode it bridges the first-edit dead zone. Reads live refs so the standing arm
  // effect and the re-arm on editor teardown install identical behavior.
  const armPenBuffer = () => {
    const container = containerRef.current;
    const buffer = penBufferRef.current;
    if (!container || !buffer || !editableRef.current) return;
    buffer.arm(container, {
      shouldBuffer: () => isInkTool(optsRef.current.tool),
      autoActivate: {
        // Read mode + a pen landing ON a page (not the empty margin/gap around it,
        // where nothing could be inked) → start writing. A pen tap in the margin
        // stays inert instead of silently flipping the doc into edit mode (F1.4).
        shouldActivate: (e) =>
          optsRef.current.tool === "none" &&
          !!(e.target as HTMLElement | null)?.closest?.("[data-page]"),
        onActivate: () => useAnnotationTool.getState().setTool(rememberedTool(docId)),
      },
    });
  };

  // Continuous pinch/pan/zoom + wheel + keyboard, driving the controller. A lone
  // finger navigates unless finger-drawing is the intent (editing, palm reject
  // off), so the session's first contact pans instead of inking.
  useViewportGestures(containerRef, controllerRef, {
    shouldFingerDraw: () => fingerDrawActive(editModeRef.current),
    // Two/three-finger tap = undo/redo on tablets — the touch analogue of Ctrl+Z/Y,
    // gated like it (edit mode only, read live via refs).
    onTwoFingerTap: () => {
      if (editModeRef.current) modelRef.current.undo();
    },
    onThreeFingerTap: () => {
      if (editModeRef.current) modelRef.current.redo();
    },
  });

  // Attach the controller to the world + lay the page stack out as a centred
  // flex column (once). The page divs' width/height come from React (committed
  // scale); the flex gap keeps the vertical rhythm without margin-collapse.
  useLayoutEffect(() => {
    const controller = controllerRef.current;
    const root = containerRef.current;
    const world = worldRef.current;
    if (!controller || !root || !world) return;
    world.style.display = "flex";
    world.style.flexDirection = "column";
    world.style.alignItems = "center";
    world.style.rowGap = `${GAP}px`;
    controller.attach(world);
    controller.setViewportSize(root.clientWidth, root.clientHeight);
    return () => {
      // Persist the final view state + force the debounced write out on unmount so
      // a tab switch / close doesn't lose the last zoom/camera (F8).
      const c = controllerRef.current;
      if (c) {
        saveViewState(docId, {
          zoom: c.scale, tx: c.tx, ty: c.ty, tool: optsRef.current.tool, updatedAt: Date.now(),
        });
      }
      flushViewState();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply a committed scale (or a page-count change) in ONE pre-paint tick. React
  // has already re-sized the page divs from `committedScale`; here we hand the new
  // content box to the controller, fold the CSS factor back to 1 (commitRaster) —
  // or seat on first layout — re-size every living editor in place, and redraw the
  // read-only overlay whose SVG was measured at the old page width. useLayoutEffect
  // (not useEffect) so it all lands before the browser paints — no flash of
  // displaced ink.
  useLayoutEffect(() => {
    const controller = controllerRef.current;
    const root = containerRef.current;
    const world = worldRef.current;
    if (!controller || !root || !world) return;
    const pageW = BASE_W * committedScale;
    const pageH = BASE_H * committedScale;
    const { contentW, contentH } = canvasContentSize(pageCount, pageW, pageH, GAP);
    world.style.width = `${contentW}px`;
    controller.setViewportSize(root.clientWidth, root.clientHeight);
    // Declare the reserve BEFORE the content size, so the first clamp already
    // knows about it. Seating alone was not enough: `seat()` ends in
    // `clampTranslate()`, whose ceiling was the bare margin, so the requested
    // offset was discarded on the very first frame and the page opened under
    // the toolbar regardless.
    controller.setTopReserve(topGapFor(pageW, !!onRename));
    controller.setContentSize(contentW, contentH);
    let commitPending = true;
    if (seatPendingRef.current) {
      // Restore the saved camera at first layout (F8); otherwise centre the stack.
      const restore = restoreRef.current || null;
      if (restore) {
        controller.seat(restore.tx, restore.ty, committedScale);
        restoreRef.current = null;
      } else {
        controller.seat((root.clientWidth - contentW) / 2, topGapFor(pageW, !!onRename), committedScale);
      }
      seatPendingRef.current = false;
      commitPending = false;
    }
    // Living editors keep their seed coordinates; re-size each to the new page
    // pixel size — a Fabric viewport zoom, no rebuild, no ink re-inflation.
    for (const ed of editors.current.values()) ed.resize(pageW, pageH);
    // AFTER the resize, never before. commitRaster drops the compensating CSS
    // magnification, which is the only thing making the old raster fill the new
    // scale. Dropping it first told the viewport the pixels were current while
    // every editor was still re-tessellating, so the transform signal led the
    // sharp pixels by 1,373.0 ms on a wheel zoom.
    if (commitPending) controller.commitRaster(committedScale);
    // A new scale changes which pages fall in the band.
    scheduleReconcileRef.current();
    if (!editModeRef.current) drawOverlays();
    updateScrollbars();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [committedScale, pageCount]);

  // Keep the controller's viewport size current (assistant collapsing, tablet
  // rotating). Re-clamp so content smaller than the viewport re-centres.
  useEffect(() => {
    const root = containerRef.current;
    const controller = controllerRef.current;
    if (!root || !controller) return;
    const ro = new ResizeObserver(() => {
      // Center-anchored resize compensation (Part A mechanism 2): a bar toggle
      // resizes this container; hold the world point at the viewport centre fixed
      // (and re-clamp) instead of letting it drift with the container edge.
      controller.resizeViewport(root.clientWidth, root.clientHeight);
    });
    ro.observe(root);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Which page editor owns a client point (its page div contains it) — routes a
  // buffered pen contact to the right page when the editors attach.
  const editorAtClientPoint = useCallback((cx: number, cy: number): PageEditor | null => {
    for (const [p, el] of pageEls.current) {
      const r = el.getBoundingClientRect();
      if (cx >= r.left && cx <= r.right && cy >= r.top && cy <= r.bottom) {
        const live = editors.current.get(p);
        if (live) return live;
        // The contact landed on a page outside the band — mount it now rather than
        // drop the stroke. One page is cheap; this is the on-demand half of
        // virtualization, and it is also what a pen contact during a fast fling
        // needs.
        const fabric = fabricRef.current;
        if (!fabric || !editModeRef.current) return null;
        const ed = new PageEditor(fabric, el, p, modelRef.current.byPage(p), persist, optsRef.current);
        editors.current.set(p, ed);
        // Same 2-frame handover as the scroll path: this one happens directly under
        // the pen, so a blank frame here is the worst place for one.
        handoverPending.current.add(p);
        requestAnimationFrame(() =>
          requestAnimationFrame(() => {
            handoverPending.current.delete(p);
            if (editModeRef.current) drawOverlays();
          }),
        );
        return ed;
      }
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persist, drawOverlays]);

  // Reconcile the editor map to the current page set, REUSING living editors
  // (never rebuilding one — north-star A) and seeding new pages from saved data.
  // No-op unless a Fabric session is active (edit mode + module loaded).
  const reconcileEditors = useCallback(() => {
    const fabric = fabricRef.current;
    if (!fabric || !editModeRef.current) return;
    // Never rebuild the editor set under an active pen: destroy() would finalize
    // the stroke early, and the next contact would land on a different canvas.
    if (isPenStrokeActive()) {
      scheduleReconcile();
      return;
    }
    const count = pageCountRef.current;
    // Only the viewport band gets a live editor. A 20-page note used to mount 20
    // editors and 60 canvas bitmaps, seed every stroke in the document into Fabric,
    // and allocate one offscreen cache canvas per stroke — all on the click that
    // enters write mode. Everything outside the band stays the read-only SVG.
    // A document that fits in the band gets every editor, and none of the band
    // machinery: see VIRTUALIZE_MIN_PAGES.
    const virtualizing = count > VIRTUALIZE_MIN_PAGES;
    const want = virtualizing
      ? pagesInBand(containerRef.current, pageEls.current)
      : new Set(Array.from({ length: count }, (_, i) => i + 1));
    let changed = false;
    for (const [p, ed] of editors.current) {
      if (p <= count && want.has(p)) continue;
      ed.destroy();
      editors.current.delete(p);
      handoverPending.current.delete(p);
      changed = true;
    }
    const mounted: number[] = [];
    for (const p of want) {
      if (p > count || editors.current.has(p)) continue; // reuse a mounted page
      const el = pageEls.current.get(p);
      if (!el) continue;
      handoverPending.current.add(p);
      mounted.push(p);
      // Seed from the MODEL, never the query cache — a rebuilt editor still gets
      // the fresh in-session ink, so the seed-race (#5b) can't strand it.
      editors.current.set(p, new PageEditor(fabric, el, p, modelRef.current.byPage(p), persist, optsRef.current));
      changed = true;
    }
    // A page that gained an editor must drop its SVG, and one that lost an editor
    // must get it back, or its ink vanishes. Only needed while virtualizing: the
    // edit-mode effect already cleared every overlay before Fabric loaded.
    //
    // The pages just mounted hand over across 2 frames. This pass restores the SVG
    // for anything that lost its editor and LEAVES the SVG on the new ones; the
    // second pass, once Fabric has painted, takes it away.
    if (changed && virtualizing) {
      drawOverlays();
      if (mounted.length) {
        requestAnimationFrame(() =>
          requestAnimationFrame(() => {
            for (const n of mounted) handoverPending.current.delete(n);
            if (!editModeRef.current) return;
            drawOverlays();
          }),
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persist, drawOverlays]);

  /** Re-pick the band after the camera settles. A pan or a zoom moves pages in and
   *  out of view, and mounting an editor per frame during a gesture would cost far
   *  more than the mount it saves. */
  const reconcileTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const reconcileRef = useRef<() => void>(() => {});
  function scheduleReconcile() {
    if (pageCountRef.current <= VIRTUALIZE_MIN_PAGES) return;
    if (reconcileTimer.current !== undefined) clearTimeout(reconcileTimer.current);
    reconcileTimer.current = setTimeout(() => {
      reconcileTimer.current = undefined;
      reconcileRef.current();
    }, 60);
  }
  reconcileRef.current = reconcileEditors;
  scheduleReconcileRef.current = scheduleReconcile;

  /** Draw the read overlay for pages a pan brought into the band. Trailing-timed for
   *  the same reason the editor reconcile is. */
  const overlayTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  fillOverlaysRef.current = () => {
    // Runs in WRITE mode too. Only the mounted pages are owned by editors; every
    // other page is showing the SVG, and a pan brings more of them into the band.
    if (overlayTimer.current !== undefined) clearTimeout(overlayTimer.current);
    overlayTimer.current = setTimeout(() => {
      overlayTimer.current = undefined;
      drawOverlays();
    }, 60);
  };
  useEffect(() => () => {
    if (overlayTimer.current !== undefined) clearTimeout(overlayTimer.current);
  }, []);
  useEffect(() => () => {
    if (reconcileTimer.current !== undefined) clearTimeout(reconcileTimer.current);
  }, []);

  // Arm the pen-down buffer for the whole annotatable session — in READ mode so
  // the first pen contact auto-activates the remembered tool (F1.4), and through
  // edit mode so the first-edit dead zone is bridged (F1.3). Keyed on `editable`
  // only (NOT editMode), so the read→edit transition the pen-down itself triggers
  // never disarms the buffer out from under the stroke it just recorded. The
  // buffer self-disarms after replay; the editor-teardown effect re-arms it.
  useEffect(() => {
    const container = containerRef.current;
    const buffer = penBufferRef.current;
    if (!editable || !container || !buffer) return;
    armPenBuffer();
    return () => buffer.disarm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editable]);

  // Edit-mode session: load Fabric once per entry, reconcile editors to the
  // current pages, and destroy every editor on exit/unmount. Gated on markupReady
  // so editors always seed from loaded data (see above). `committedScale` is
  // deliberately NOT a dep: a zoom commit re-sizes the living editors in place
  // (see the layout effect), it never rebuilds them.
  useEffect(() => {
    if (!editMode || !markupReady) return;
    let cancelled = false;
    clearOverlays(); // hide every read-only SVG before Fabric takes over
    loadFabric()
      .then((fabric) => {
        if (cancelled) return;
        fabricRef.current = fabric;
        // reconcileEditors() seeds every PageEditor from optsRef.current (a ref
        // reassigned each render, line ~186), so editors always take the LIVE
        // tool — this supersedes #47's manual live-store seeding (which patched
        // the old stale-`tool`-closure build path this rewrite replaced).
        reconcileEditors();
        // Editors are live — replay any pen contact buffered during the dead zone.
        penBufferRef.current?.attachEditors(editorAtClientPoint);
      })
      .catch(() => {
        if (cancelled) return;
        toast.error("Couldn't load the annotation tools");
        useAnnotationTool.getState().setTool("none");
      });
    return () => {
      cancelled = true;
      fabricRef.current = null;
      for (const ed of editors.current.values()) ed.destroy();
      editors.current.clear();
      drawOverlays(); // restore the read-only overlay now Fabric is gone
      // The buffer self-disarmed after its replay; re-arm it so a return to read
      // mode can auto-activate on the next pen contact (F1.4).
      armPenBuffer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, markupReady]);

  // Page add/remove while editing: reconcile the map WITHOUT rebuilding survivors
  // (create only the new page's editor; destroy only a removed page's).
  useEffect(() => {
    reconcileEditors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageCount]);

  // Push tool/style changes to every live page editor, one field per effect.
  // (Palm / finger-draw is no longer an editor field — the input policy reads it
  // live.)
  useEditorToolSync(() => editors.current.values(), editMode, style);

  // Read-only overlays (not editing) — redraw when edit mode toggles (clear on
  // enter, restore on exit). Data changes flow through the seed/reconcile effect;
  // committed-scale + page-count changes are handled in the layout effect (where
  // the SVG can be re-measured at the new page width, post-paint-safe).
  useEffect(() => {
    drawOverlays();
  }, [editMode, drawOverlays]);

  // Destroy the living editors for every page >= `fromPage` so the pageCount
  // reconcile effect re-creates them, RESEEDING each from the renumbered model — a
  // legitimate teardown (north-star A only forbids destroying an editor whose page
  // is unchanged; a page op genuinely renumbers these). Editors for pages below the
  // op are untouched and keep writing.
  const destroyEditorsFrom = (fromPage: number) => {
    for (const [p, ed] of editors.current) {
      if (p >= fromPage) {
        ed.destroy();
        editors.current.delete(p);
      }
    }
  };

  const addPage = async () => {
    if (pageCount >= MAX_PAGES) return; // clamped to the server cap (disabled in UI too)
    setPagesBusy(true);
    const n = pageCount + 1;
    setPageCount(n);
    // addPage keeps the legacy count endpoint (adds a blank page at the end — no ink
    // to shift, so the simpler set-count call suffices); delete/insert-in-the-middle
    // go through the dedicated per-page endpoints below.
    try {
      await api.request("POST", `/api/documents/${docId}/canvas-pages`, { count: n });
      void queryClient.invalidateQueries({ queryKey: keys.document(docId) });
    } catch (e) {
      setPageCount(n - 1); // roll the optimistic add back
      toast.error("Couldn't add the page", { description: String(e) });
    } finally {
      setPagesBusy(false);
    }
  };

  // Undo of a page delete (plan F9): re-open the page on the server FIRST (so the
  // ink re-creation that follows lands on the reopened, not-yet-shifted page), then
  // mirror the shift locally and re-create the removed ink as fresh rows. New server
  // ids are fine (recoverable, not id-stable).
  const undoPageDelete = async (index: number, captured: Annotation[]) => {
    await model.settle(); // drain in-flight creates before this shift endpoint fires
    const count = pageCountRef.current;
    try {
      await api.request("POST", `/api/documents/${docId}/canvas-pages/${index - 1}/insert`);
    } catch (e) {
      toast.error("Couldn't restore the page", { description: String(e) });
      return;
    }
    void queryClient.invalidateQueries({ queryKey: keys.document(docId) });
    model.insertPageLocal(index - 1); // shift pages >= index up, reopening page `index`
    model.recreate(captured); // re-persist the removed ink at page `index` (fresh ids)
    destroyEditorsFrom(index);
    setPageCount(count + 1);
  };

  const redoPageDelete = async (index: number) => {
    await model.settle(); // drain in-flight creates before this shift endpoint fires
    const count = pageCountRef.current;
    const captured = model.deletePageLocal(index);
    destroyEditorsFrom(index);
    setPageCount(count - 1);
    try {
      await api.request("DELETE", `/api/documents/${docId}/canvas-pages/${index}`);
      void queryClient.invalidateQueries({ queryKey: keys.document(docId) });
    } catch (e) {
      model.insertPageLocal(index - 1);
      model.restoreLocal(captured);
      destroyEditorsFrom(index);
      setPageCount(count);
      toast.error("Couldn't delete the page", { description: String(e) });
    }
  };

  // Delete page `index`: capture its ink for undo, apply the shift optimistically,
  // call the endpoint, and roll everything back on failure (the server rows were
  // never touched). A composite undo entry makes a mis-tap recoverable — retiring
  // the old silent-destroy removeLastPage.
  const deletePage = async (index: number) => {
    if (pageCountRef.current <= 1) return; // a canvas keeps at least one page
    setPagesBusy(true);
    try {
      // Drain in-flight creates BEFORE the shift: a create still carrying its
      // pre-shift page must land server-side before DELETE renumbers, or it strands
      // on the wrong page (see InkModel.settle).
      await model.settle();
      const count = pageCountRef.current;
      const captured = model.deletePageLocal(index);
      destroyEditorsFrom(index);
      setPageCount(count - 1);
      try {
        await api.request("DELETE", `/api/documents/${docId}/canvas-pages/${index}`);
        void queryClient.invalidateQueries({ queryKey: keys.document(docId) });
        model.pushCommand(
          () => undoPageDelete(index, captured),
          () => redoPageDelete(index),
        );
      } catch (e) {
        model.insertPageLocal(index - 1); // reverse the optimistic down-shift
        model.restoreLocal(captured); // re-add the removed ink (original ids, no re-sync)
        destroyEditorsFrom(index);
        setPageCount(count);
        toast.error("Couldn't delete the page", { description: String(e) });
      }
    } finally {
      setPagesBusy(false);
    }
  };

  const undoPageInsert = async (index: number) => {
    await model.settle(); // drain in-flight creates before this shift endpoint fires
    const count = pageCountRef.current;
    const captured = model.deletePageLocal(index + 1); // remove the (empty) inserted page
    destroyEditorsFrom(index + 1);
    setPageCount(count - 1);
    try {
      await api.request("DELETE", `/api/documents/${docId}/canvas-pages/${index + 1}`);
      void queryClient.invalidateQueries({ queryKey: keys.document(docId) });
    } catch (e) {
      model.insertPageLocal(index);
      model.restoreLocal(captured);
      destroyEditorsFrom(index + 1);
      setPageCount(count);
      toast.error("Couldn't remove the page", { description: String(e) });
    }
  };

  const redoPageInsert = async (index: number) => {
    await model.settle(); // drain in-flight creates before this shift endpoint fires
    const count = pageCountRef.current;
    model.insertPageLocal(index);
    destroyEditorsFrom(index + 1);
    setPageCount(count + 1);
    try {
      await api.request("POST", `/api/documents/${docId}/canvas-pages/${index}/insert`);
      void queryClient.invalidateQueries({ queryKey: keys.document(docId) });
    } catch (e) {
      model.deletePageLocal(index + 1);
      destroyEditorsFrom(index + 1);
      setPageCount(count);
      toast.error("Couldn't add the page", { description: String(e) });
    }
  };

  // Insert an empty page AFTER page `index` (the "insert below" affordance): shift
  // ink on later pages down one, open a blank page at index+1, persist, undoable.
  const insertPageBelow = async (index: number) => {
    if (pageCountRef.current >= MAX_PAGES) return;
    setPagesBusy(true);
    try {
      // Drain in-flight creates before the shift (see deletePage / InkModel.settle).
      await model.settle();
      const count = pageCountRef.current;
      model.insertPageLocal(index);
      destroyEditorsFrom(index + 1);
      setPageCount(count + 1);
      try {
        await api.request("POST", `/api/documents/${docId}/canvas-pages/${index}/insert`);
        void queryClient.invalidateQueries({ queryKey: keys.document(docId) });
        model.pushCommand(
          () => undoPageInsert(index),
          () => redoPageInsert(index),
        );
      } catch (e) {
        model.deletePageLocal(index + 1); // remove the empty page just added, shift back up
        destroyEditorsFrom(index + 1);
        setPageCount(count);
        toast.error("Couldn't add the page", { description: String(e) });
      }
    } finally {
      setPagesBusy(false);
    }
  };

  const saveAsPdf = async () => {
    setSaving(true);
    try {
      const { file_url } = await api.request<{ file_url: string }>(
        "POST",
        `/api/documents/${docId}/canvas-pdf`,
      );
      window.open(file_url, "_blank");
      toast.success("Saved a PDF cropped to your note");
    } catch (e) {
      toast.error("Couldn't save the PDF", { description: String(e) });
    } finally {
      setSaving(false);
    }
  };

  const zoomBy = (factor: number) => {
    const controller = controllerRef.current;
    if (controller) controller.zoomTo(controller.scale * factor);
  };

  const pageW = BASE_W * committedScale;
  const pageH = BASE_H * committedScale;
  // Paper (plan §3.5): a CSS background layered over the page's fill colour, its
  // cell sized in committed px so the lines track the page at every zoom (during a
  // live pinch it rides the world's CSS transform with the page, then re-renders
  // crisp on commit). Behind every ink layer — it's the page div's own background.
  // Whole pixels: a repeating gradient at a fractional cell re-rasterizes with
  // its hard stop on a different sub-pixel boundary, which reads as a shimmer on
  // every zoom commit. The pattern rides the world's CSS transform during the
  // pinch itself, so this only has to be crisp at rest.
  const paperBg = paperBackground(
    paperStyle,
    Math.round(PAPER_UNIT * paperSpacing * committedScale),
    Math.max(1, Math.round(committedScale)),
  );

  return (
    <div className="flex h-full min-h-0 flex-col bg-[#1b1f1a]">
      {/* FIXED height + no-wrap: entering annotate mode / switching tools expands
          the AnnotationToolbar, which must scroll internally rather than reflow the
          chrome height and shift the canvas below it (Part A mechanism 1). */}
      <div className="flex h-9 shrink-0 flex-nowrap items-center gap-2 px-3 py-1 text-muted-foreground">
        {/* Fullscreen collapses the document's title bar, so while writing there
            was nothing on screen naming the note. This row is the surface's own
            chrome and it is always visible, so the title moves here — the label
            it replaces is the least useful thing on the row. */}
        {fullscreen && title ? (
          <span className="min-w-0 truncate text-[12px] text-foreground/90" title={title}>
            {title}
          </span>
        ) : (
          <span className="shrink-0 font-mono text-[11px]">notebook · {pageCount}pp</span>
        )}
        {/* These sit NEXT TO the label, not at the right edge. The reader floats
            its document-controls island over that corner (see ReaderTab), and a
            right-aligned cluster lands underneath it — every one of these
            buttons was unclickable, covered by the island. Leaving the corner to
            the island beats reserving its width here, which would silently break
            again the moment the island gains a button. */}
        <div className="ml-3 flex items-center gap-1">
          <Tip label="Remove the last page">
            <Button variant="ghost" size="icon" aria-label="Remove the last page" onClick={() => void deletePage(pageCount)} disabled={pagesBusy || pageCount <= 1}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </Tip>
          <Tip label={pageCount >= MAX_PAGES ? "Maximum 100 pages" : "Add a page"}>
            <Button variant="ghost" size="icon" aria-label="Add a page" onClick={() => void addPage()} disabled={pagesBusy || pageCount >= MAX_PAGES}>
              <FilePlus2 className="h-3.5 w-3.5" />
            </Button>
          </Tip>
          <Tip label="Save as PDF (cropped to your note)">
            <Button variant="ghost" size="icon" onClick={() => void saveAsPdf()} disabled={saving}>
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />}
            </Button>
          </Tip>
          <Tip label="Zoom out">
            <Button variant="ghost" size="icon" onClick={() => zoomBy(1 / 1.2)} aria-label="Zoom out">
              <Minus className="h-3.5 w-3.5" />
            </Button>
          </Tip>
          <span className="w-10 text-center font-mono text-[10px] text-muted-foreground">{Math.round(displayScale * 100)}%</span>
          <Tip label="Zoom in">
            <Button variant="ghost" size="icon" onClick={() => zoomBy(1.2)} aria-label="Zoom in">
              <Plus className="h-3.5 w-3.5" />
            </Button>
          </Tip>
        </div>
      </div>

      <div className="relative min-h-0 flex-1">
      <div
        ref={containerRef}
        tabIndex={0}
        style={{ padding: 0, touchAction: "none" }}
        className={`pdf-scroll absolute inset-0 overflow-hidden outline-none${editMode ? " gv-editing" : ""}`}
      >
        <div ref={worldRef} className="pdf-world">
          {Array.from({ length: pageCount }, (_, i) => i + 1).map((p) => (
            <div key={p} className="group relative" style={{ width: pageW }} data-page={p}>
              <div
                ref={(el) => {
                  if (el) pageEls.current.set(p, el);
                  else pageEls.current.delete(p);
                }}
                className="relative rounded-sm shadow-deep ring-1 ring-black/10"
                style={{ backgroundColor: background, width: pageW, height: pageH, ...(paperBg ?? {}) }}
              />
              {/* The title heads page 1 from the MARGIN, never from the sheet.
                  On the sheet it either eats strokes or has to refuse the
                  pointer to avoid eating them, and refusing made it
                  unclickable. Up here it is simply a field. */}
              {p === 1 && onRename && (
                <PageTitle
                  title={title ?? ""}
                  onRename={onRename}
                  editable={editable}
                  createdAt={createdAt}
                  pageWidth={pageW}
                />
              )}
              <span className="pointer-events-none absolute -top-4 right-0 font-mono text-[10px] text-muted-foreground">
                {p} / {pageCount}
              </span>
              {/* Per-page management (plan F9): a small, unobtrusive cluster —
                  hover-revealed on a fine pointer, always faintly visible on
                  touch (see .gv-page-chrome). In the margin, not over the
                  inkable surface, so its targets never eat a stroke.
                  BELOW the sheet, not above it: above, it shares the top strip
                  with the annotation toolbar, which covers it on a tablet — and
                  "insert a page below this one" reads better from beneath the
                  page than from over its header. */}
              {editMode && (
                <div className="gv-page-chrome absolute -bottom-5 left-0 flex items-center gap-1">
                  <Tip label="Insert page below">
                    <button
                      type="button"
                      aria-label={`Insert page below page ${p}`}
                      onClick={() => void insertPageBelow(p)}
                      disabled={pagesBusy || pageCount >= MAX_PAGES}
                      className="grid h-5 w-5 place-items-center rounded bg-card/90 text-muted-foreground shadow-sm ring-1 ring-black/20 backdrop-blur transition-colors hover:text-foreground disabled:opacity-30"
                    >
                      <FilePlus2 className="h-3 w-3" />
                    </button>
                  </Tip>
                  <Tip label="Delete page">
                    <button
                      type="button"
                      aria-label={`Delete page ${p}`}
                      onClick={() => void deletePage(p)}
                      disabled={pagesBusy || pageCount <= 1}
                      className="grid h-5 w-5 place-items-center rounded bg-card/90 text-muted-foreground shadow-sm ring-1 ring-black/20 backdrop-blur transition-colors hover:text-destructive disabled:opacity-30"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </Tip>
                </div>
              )}
            </div>
          ))}
        </div>
        {scrollbars}
        {markup.isLoading && (
          <div className="pointer-events-none absolute inset-0 grid place-items-center">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
          </div>
        )}
      </div>
        {editable && <AnnotationToolbar fonts={fonts} model={model} paper={isLightPaper(background) ? "light" : "dark"} />}
      </div>
    </div>
  );
}
